// DTO ⇄ view model for both link wizards, plus the two REST link bodies.
import { formatDateOrDash, daysUntil } from '@/lib/format/date';
import { formatTextOrDash } from '@/lib/format/text';
import { formatPercentOrDash } from '@/lib/format/number';
import {
  buildQuantitiesByUnit,
  buildRawQuantitiesByUnit,
  defaultUnitType,
} from '@/lib/mappers/quantities';
import { processDiscrepancyItems } from '@/lib/mappers/discrepancies';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import {
  contractStatusLabel,
  orderDisplayStatusLabel,
  outgoingShipmentStatusLabel,
} from '@/config/statuses';
import { EMPTY_LIST } from '@/lib/utils';
import {
  allocationPreferenceLabel,
  feedstockCategoryLabel,
  toAllocationPreferenceArray,
} from '@/config/formOptions';

const formatGhg = (value) => (value == null ? '-' : `${value} g CO₂e/MJ`);

const SUGGESTION_GROUP = 'SUGGESTION';

// The server's ranking and the reasons a row is an "other" — `group` is SUGGESTION | OTHER.
const processSuggestion = (node) => ({
  isSuggestion: node.group === SUGGESTION_GROUP,
  sustainabilityMismatches: node.sustainabilityMismatches ?? EMPTY_LIST,
});

// `pageInfo` describes `nodes`; a wizard that draws one page needs to know it was cut short.
const processLinkablePage = (connection, processRow) => ({
  rows: (connection?.nodes ?? []).map(processRow),
  totalCount: connection?.pageInfo?.total ?? 0,
  hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
});

// The three unit bases a linkable-shipment row can be read in.
const buildShipmentByUnit = (node) => {
  const shipment = node.shipment ?? {};
  return buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: shipment.workingUnit,
      amounts: {
        assignedQuantity: node.assignedQuantityWorking,
        unassignedQuantity: shipment.unassignedContractQuantityWorking,
        shipmentQuantity: shipment.shipmentQuantityWorking,
      },
    },
    [UNIT_TYPES.declaration]: {
      unit: shipment.declarationUnit,
      amounts: {
        assignedQuantity: node.assignedQuantityDeclaration,
        unassignedQuantity: shipment.unassignedContractQuantityDeclaration,
        shipmentQuantity: shipment.shipmentQuantityDeclaration,
      },
    },
    [UNIT_TYPES.tracking]: {
      unit: shipment.trackingUnit,
      amounts: {
        assignedQuantity: node.assignedQuantityTracking,
        unassignedQuantity: shipment.unassignedContractQuantityTracking,
        shipmentQuantity: shipment.shipmentQuantityTracking,
      },
    },
  });
};

// The raw amount behind a rendered cell, so the assign-quantity cap can compare numbers.
const rawShipmentUnassigned = (node) => ({
  [UNIT_TYPES.working]: node.shipment?.unassignedContractQuantityWorking ?? null,
  [UNIT_TYPES.declaration]: node.shipment?.unassignedContractQuantityDeclaration ?? null,
  [UNIT_TYPES.tracking]: node.shipment?.unassignedContractQuantityTracking ?? null,
});

// One `linkableShipmentsForContract` node → a row view model; keys match the column ids.
export const processLinkableShipment = (node) => {
  const shipment = node.shipment ?? {};
  const byUnit = buildShipmentByUnit(node);
  return {
    id: shipment.id,
    shipmentId: formatTextOrDash(shipment.shipmentId),
    totalContractsLinked: shipment.totalContractsLinked ?? '-',
    byUnit,
    unitType: defaultUnitType(byUnit),
    unassignedByUnit: rawShipmentUnassigned(node),
    ghgEmissions: formatGhg(shipment.ghgEmissions),
    ghgEmissionSavings: formatPercentOrDash(shipment.ghgEmissionSavings),
    rawMaterialType: formatTextOrDash(shipment.rawMaterialType),
    countryOfOrigin: formatTextOrDash(shipment.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(shipment.feedstockCategory)),
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(shipment.preferredAllocation)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    dateOfDispatch: formatDateOrDash(shipment.dateOfDispatch),
    discrepancyCount: shipment.discrepancyCount ?? 0,
    linkedContractIds: node.linkedContractIds ?? EMPTY_LIST,
    ...processSuggestion(node),
  };
};

export const processLinkableShipments = (connection) =>
  processLinkablePage(connection, processLinkableShipment);

// `linkedContractIds` holds contract codes; assigned quantities can be null on a real link.
export const isShipmentLinkedToContract = (row, contractCode) =>
  Boolean(contractCode) && (row?.linkedContractIds ?? EMPTY_LIST).includes(contractCode);

// A contract has no tracking unit, so a linkable-contract row reads in two bases.
const buildContractByUnit = (node) => {
  const contract = node.contract ?? {};
  return buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: contract.workingUnit,
      amounts: {
        assignedQuantity: node.assignedQuantityWorking,
        contractedQuantity: contract.workingQuantity,
        shipmentQuantity: contract.shippedWorkingQuantity,
        outstandingQuantity: contract.outstandingWorkingQuantity,
      },
    },
    [UNIT_TYPES.declaration]: {
      unit: contract.declarationUnit,
      amounts: {
        assignedQuantity: node.assignedQuantityDeclaration,
        contractedQuantity: contract.declarationQuantity,
        shipmentQuantity: contract.shippedDeclarationQuantity,
        outstandingQuantity: contract.outstandingDeclarationQuantity,
      },
    },
  });
};

// What the assign-quantity cell caps against — a contract can still take its outstanding.
const rawContractOutstanding = (node) => ({
  [UNIT_TYPES.working]: node.contract?.outstandingWorkingQuantity ?? null,
  [UNIT_TYPES.declaration]: node.contract?.outstandingDeclarationQuantity ?? null,
});

// One `linkableContractsForShipment` node → a row view model; keys match the column ids.
export const processLinkableContract = (node) => {
  const contract = node.contract ?? {};
  const byUnit = buildContractByUnit(node);
  return {
    id: contract.id,
    contractId: formatTextOrDash(contract.contractId),
    status: contractStatusLabel(contract.status) ?? '-',
    totalShipmentLinked: contract.linkedShipmentCount ?? '-',
    byUnit,
    unitType: defaultUnitType(byUnit),
    unassignedByUnit: rawContractOutstanding(node),
    ghgEmissions: formatGhg(contract.ghgEmissions),
    ghgEmissionSavings: formatPercentOrDash(contract.ghgEmissionSavings),
    rawMaterialType: formatTextOrDash(contract.rawMaterialType),
    countryOfOrigin: formatTextOrDash(contract.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(contract.feedstockCategory)),
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(contract.preferredAllocation)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    deliverBy: formatDateOrDash(contract.deliverBy),
    discrepancyCount: contract.discrepancyCount ?? 0,
    discrepancyItems: processDiscrepancyItems(contract.discrepancies?.items),
    linkedShipmentIds: node.linkedShipmentIds ?? EMPTY_LIST,
    ...processSuggestion(node),
  };
};

export const processLinkableContracts = (connection) =>
  processLinkablePage(connection, processLinkableContract);

// Column id → a `PendingOrderOrderByField`. The enum offers nothing else the wizard shows.
const LINKABLE_ORDER_SORT_FIELDS = Object.freeze({
  orderNo: 'orderNo',
  contractedQuantity: 'contractedQuantity',
  dispatchBy: 'dispatchDate',
});

// Table's `{ id, dir }` → `orderBy`; an unmapped column sends nothing rather than failing the query.
export const buildLinkableOrderOrderBy = (sort) => {
  const field = LINKABLE_ORDER_SORT_FIELDS[sort?.id];
  return field ? [{ field, direction: sort.dir ?? 'asc' }] : undefined;
};

// PendingOrderSearch ANDs its fields server-side, so one box drives the order no only.
export const buildOrderSearchInput = (search) => {
  const term = search?.trim();
  if (!term) return undefined;
  return { orderNo: { contains: term, mode: 'insensitive' } };
};

// Raw: AssignQuantityCell compares the typed number against this cap — the server's remaining-to-ship.
const buildLinkableOrderCaps = (node) =>
  buildRawQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: node.order?.workingUnit,
      amounts: { cap: node.unassignedQuantityWorking },
    },
    [UNIT_TYPES.declaration]: {
      unit: node.order?.declarationUnit,
      amounts: { cap: node.unassignedQuantityDeclaration },
    },
  });

// The assigned share is in the SHIPMENT's unit; it shows only where that is the order's unit too.
const assignedInOrderUnit = (assigned, shipmentUnit, orderUnit) =>
  shipmentUnit === orderUnit ? assigned : null;

const buildLinkableOrderByUnit = (node) => {
  const order = node.order ?? {};
  return buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: order.workingUnit,
      amounts: {
        assignedQuantity: assignedInOrderUnit(
          node.assignedQuantityWorking,
          node.workingUnit,
          order.workingUnit,
        ),
        unassignedQuantity: node.unassignedQuantityWorking,
        contractedQuantity: order.contractedWorkingQuantity,
        shippedQuantity: order.shippedWorkingQuantity,
        remainingToShipQuantity: order.remainingToShipWorkingQuantity,
        allocatedQuantity: order.allocatedWorkingQuantity,
        remainingToAllocateQuantity: order.remainingToAllocateWorkingQuantity,
      },
    },
    [UNIT_TYPES.declaration]: {
      unit: order.declarationUnit,
      amounts: {
        assignedQuantity: assignedInOrderUnit(
          node.assignedQuantityDeclaration,
          node.declarationUnit,
          order.declarationUnit,
        ),
        unassignedQuantity: node.unassignedQuantityDeclaration,
        contractedQuantity: order.contractedDeclarationQuantity,
        shippedQuantity: order.shippedDeclarationQuantity,
        remainingToShipQuantity: order.remainingToShipDeclarationQuantity,
        allocatedQuantity: order.allocatedDeclarationQuantity,
        remainingToAllocateQuantity: order.remainingToAllocateDeclarationQuantity,
      },
    },
  });
};

// `node` is a LinkableShipmentOrder — the order weighed against the shipment it may link to.
const processLinkableOrder = (node) => {
  const order = node.order ?? {};
  const byUnit = buildLinkableOrderByUnit(node);
  const caps = buildLinkableOrderCaps(node);
  return {
    id: order.id,
    orderNo: formatTextOrDash(order.orderNo),
    status: orderDisplayStatusLabel(order.status) ?? '-',
    materialName: formatTextOrDash(order.material?.name),
    terminal: formatTextOrDash(order.terminal),
    soldTo: formatTextOrDash(order.customerAccount?.companyname),
    shippingTo: formatTextOrDash(order.shippingDetails?.shippingTo),
    byUnit,
    unitType: defaultUnitType(byUnit),
    // AssignQuantityCell reads this map by unit type for the row's own ceiling.
    unassignedByUnit: Object.fromEntries(
      Object.entries(caps).map(([unitType, amounts]) => [unitType, amounts.cap]),
    ),
    tolerance: formatPercentOrDash(order.tolerance),
    // Counted across every shipment the order is linked to, not just the one being picked for.
    totalShipmentsLinked: formatTextOrDash(String(node.totalOutgoingShipmentsLinked || '')),
    ghgEmissions: formatGhg(order.ghgEmission),
    ghgSavings: formatPercentOrDash(order.ghgSaving),
    rawMaterialType: formatTextOrDash(order.rawMaterialType),
    countryOfOrigin: formatTextOrDash(order.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(order.feedstockCategory)),
    // preferredAllocation is a single scalar, not a list — never assume an array.
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(order.preferredAllocation)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    dispatchBy: formatDateOrDash(order.dispatchDate),
    // Figma's warning counts down to the dispatch date; the server sends no message of its own.
    daysToDispatch: daysUntil(order.dispatchDate),
    discrepancyCount: order.discrepancyCount ?? 0,
    // A null assigned quantity is the server's "not linked to this shipment".
    isLinked: node.assignedQuantityDeclaration != null,
  };
};

// Every key the save route matches on; an absent one is omitted, not sent null.
export const buildLinkableShipmentFilters = ({ materialId, siteId, customerAccountId } = {}) => ({
  materialId: materialId == null ? undefined : [Number(materialId)],
  siteId: siteId == null ? undefined : [Number(siteId)],
  customerAccountId: customerAccountId == null ? undefined : [Number(customerAccountId)],
});

// `OutgoingShipmentSearch` spells it `shipmentNo`, unlike the shipments list's own `shipmentId`.
export const buildLinkableShipmentSearchInput = (search) => {
  const term = search?.trim();
  if (!term) return undefined;
  return { shipmentNo: { contains: term, mode: 'insensitive' } };
};

// Column id → an `OutgoingShipmentOrderByField`; the enum offers nothing else the wizard shows.
const LINKABLE_SHIPMENT_SORT_FIELDS = Object.freeze({
  shipmentId: 'shipmentNo',
  dateOfDispatch: 'dispatchDate',
  totalQuantity: 'totalQuantity',
});

export const buildLinkableShipmentOrderBy = (sort) => {
  const field = LINKABLE_SHIPMENT_SORT_FIELDS[sort?.id];
  return field ? [{ field, direction: sort.dir ?? 'asc' }] : undefined;
};

// The ceiling is what the shipment still has spare, exactly as the order side caps on its own.
const buildLinkableShipmentCaps = (node) =>
  buildRawQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: node.workingUnit,
      amounts: { cap: node.unassignedQuantityWorking },
    },
    [UNIT_TYPES.declaration]: {
      unit: node.declarationUnit,
      amounts: { cap: node.unassignedQuantityDeclaration },
    },
  });

const buildLinkableShipmentByUnit = (node) =>
  buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: node.workingUnit,
      amounts: {
        unassignedQuantity: node.unassignedQuantityWorking,
        shipmentQuantity: node.totalQuantityWorking,
        allocatedQuantity: node.allocatedQuantityWorking,
        remainingToAllocateQuantity: node.remainingToAllocateQuantityWorking,
      },
    },
    [UNIT_TYPES.declaration]: {
      unit: node.declarationUnit,
      amounts: {
        unassignedQuantity: node.unassignedQuantityDeclaration,
        shipmentQuantity: node.totalQuantityDeclaration,
        allocatedQuantity: node.allocatedQuantityDeclaration,
        remainingToAllocateQuantity: node.remainingToAllocateQuantityDeclaration,
      },
    },
  });

// One outgoing-shipment candidate — the order side's mirror of processLinkableShipment.
const processLinkableOutgoingShipment = (node) => {
  const byUnit = buildLinkableShipmentByUnit(node);
  const caps = buildLinkableShipmentCaps(node);
  return {
    id: node.id,
    shipmentId: formatTextOrDash(node.shipmentNo),
    status: outgoingShipmentStatusLabel(node.status) ?? '-',
    materialName: formatTextOrDash(node.material?.name),
    facilityName: formatTextOrDash(node.site?.siteName),
    terminal: formatTextOrDash(node.terminal),
    soldTo: formatTextOrDash(node.customerAccount?.companyname),
    shippingTo: formatTextOrDash(node.shippingDetails?.shippingTo),
    byUnit,
    unitType: defaultUnitType(byUnit),
    // AssignQuantityCell reads this map by unit type for the row's own ceiling.
    unassignedByUnit: Object.fromEntries(
      Object.entries(caps).map(([unitType, amounts]) => [unitType, amounts.cap]),
    ),
    linkedOrders: formatTextOrDash(String((node.linkedOrders ?? []).length || '')),
    linkedOrderNos: (node.linkedOrders ?? EMPTY_LIST).map((order) => order.orderNo),
    ghgEmissions: formatGhg(node.ghgEmission),
    ghgSavings: formatPercentOrDash(node.ghgSaving),
    rawMaterialType: formatTextOrDash(node.rawMaterialType),
    countryOfOrigin: formatTextOrDash(node.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(node.feedstockCategory)),
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(node.preferredAllocation)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    dateOfDispatch: formatDateOrDash(node.dispatchDate),
    discrepancyCount: node.discrepancyCount ?? 0,
  };
};

export const processLinkableShipmentsForOrder = (connection) =>
  processLinkablePage(connection, processLinkableOutgoingShipment);

export const processLinkableOrders = (connection) =>
  processLinkablePage(connection, processLinkableOrder);

/**
 * `{ rowId, quantity, unitSymbol }[]` → the link-orders body. `assignedQuantityUnit` must be the
 * unit's SYMBOL — the server compares it against the shipment's display unit to read the basis.
 */
export const buildOrderLinksPayload = (entries) =>
  entries.map((entry) => ({
    id: Number(entry.rowId),
    assignedQuantity: Number(entry.quantity),
    assignedQuantityUnit: entry.unitSymbol,
  }));

// ShipmentSearch ANDs its fields server-side, so one box drives the shipment id only.
// TODO: search material + supplier too, once the server ORs them.
export const buildShipmentSearchInput = (search) => {
  const term = search?.trim();
  if (!term) return undefined;
  return { shipmentId: { contains: term, mode: 'insensitive' } };
};

// ContractSearch ANDs its fields server-side, so one box drives the contract id only.
// TODO: search material + supplier too, once the server ORs them.
export const buildContractSearchInput = (search) => {
  const term = search?.trim();
  if (!term) return undefined;
  return { contractId: { contains: term, mode: 'insensitive' } };
};

/**
 * `{ rowId, quantity, unitSymbol }[]` → a `shipmentLinks` body. `assignedUnit` must be the
 * unit's SYMBOL — the server matches it against the material's own units and 400s otherwise.
 */
export const buildShipmentLinksPayload = (entries) =>
  entries.map((entry) => ({
    batchGroupId: Number(entry.rowId),
    assignedQuantity: Number(entry.quantity),
    assignedUnit: entry.unitSymbol,
  }));

// The mirror body: one shipment, one entry per contract.
export const buildContractLinksPayload = (entries) =>
  entries.map((entry) => ({
    contractDbId: Number(entry.rowId),
    assignedQuantity: Number(entry.quantity),
    assignedUnit: entry.unitSymbol,
  }));

// A row is a suggestion, or already linked, only if it is so for EVERY record being linked.
const SHARED_FLAGS = Object.freeze(['isSuggestion', 'isLinked']);

// Rows every selected row can link — a candidate must serve all of them.
export const intersectLinkableRows = (pages) => {
  const lists = pages.filter(Boolean).map((page) => page.rows ?? []);
  if (lists.length === 0) return [];
  const [first, ...rest] = lists;
  const restById = rest.map((rows) => new Map(rows.map((row) => [row.id, row])));
  return first
    .filter((row) => restById.every((rows) => rows.has(row.id)))
    .map((row) =>
      SHARED_FLAGS.reduce(
        (merged, flag) =>
          flag in row
            ? { ...merged, [flag]: restById.every((rows) => rows.get(row.id)[flag] && row[flag]) }
            : merged,
        row,
      ),
    );
};

// A linkable row's discrepancy popup — one shipment, only the fields the popup shows.
export const processShipmentDiscrepancy = (shipment) => {
  const byUnit = buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: shipment?.workingUnit,
      amounts: {
        shipmentQuantity: shipment?.shipmentQuantityWorking,
        receivedDeclarationQuantity: shipment?.receivedDeclarationQuantityWorking,
        availableQuantity: shipment?.availableShipmentQuantityWorking,
      },
    },
    [UNIT_TYPES.declaration]: {
      unit: shipment?.declarationUnit,
      amounts: {
        shipmentQuantity: shipment?.shipmentQuantityDeclaration,
        receivedDeclarationQuantity: shipment?.receivedDeclarationQuantityDeclaration,
        availableQuantity: shipment?.availableShipmentQuantityDeclaration,
      },
    },
    [UNIT_TYPES.tracking]: {
      unit: shipment?.trackingUnit,
      amounts: {
        shipmentQuantity: shipment?.shipmentQuantityTracking,
        receivedDeclarationQuantity: shipment?.receivedDeclarationQuantityTracking,
        availableQuantity: shipment?.availableShipmentQuantityTracking,
      },
    },
  });

  return {
    id: shipment?.id,
    shipmentId: formatTextOrDash(shipment?.shipmentId),
    materialName: formatTextOrDash(shipment?.material?.name),
    supplierName: formatTextOrDash(shipment?.sentBySupplier?.companyname),
    facilityName: formatTextOrDash(shipment?.receivedAtSite?.siteName),
    terminal: formatTextOrDash(shipment?.terminal),
    unitType: defaultUnitType(byUnit),
    byUnit,
    discrepancies: processDiscrepancyItems(shipment?.discrepancies),
  };
};

// The link save answers with the shipment's FULL linked list — the form's chips.
export const processLinkedContractLinks = (dto) =>
  (dto?.output?.data ?? []).map((link) => ({
    linkId: link.linkId ?? null,
    id: link.id ?? null,
    contractId: link.contractId ?? '',
    density: link.density ?? null,
  }));

// One declaration, one entry per shipment — same shape as the contract side's.
export const buildDeclarationShipmentLinksPayload = buildShipmentLinksPayload;
